import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Loader2,
  KeyRound,
  Zap,
  Users,
  Target,
  CheckCircle,
  Building,
  FileCheck,
  Fingerprint,
  Cloud,
  Cpu,
  Shield,
  Database,
  TrendingUp,
  Globe,
  Smartphone,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { clearActiveClient } from "@/utils/clientWorkspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    account_type: "individual",
    organization_name: "",
    gst_number: "",
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState({
    login: false,
    register: false,
  });
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Show session expired message if redirected from auto-logout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("session_expired") === "true") {
      toast.error("Session expired", {
        description: "Your session has expired. Please login again.",
        duration: 5000,
      });
      // Clean up URL
      window.history.replaceState({}, "", "/login");
    }
  }, [location]);

  // Check for saved credentials
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setLoginData((prev) => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, loginData);
      clearActiveClient();
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", loginData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      toast.success("Welcome back!", {
        description: "Login successful",
      });

      setTimeout(() => {
        if (response.data.user.role === "admin") {
          navigate("/admin");
        } else if (response.data.user.account_type === "team_member") {
          navigate("/team-members");
        } else {
          navigate(from);
        }
      }, 1000);
    } catch (error) {
      toast.error("Login failed", {
        description:
          error.response?.data?.detail || "Please check your credentials",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Validate organization fields if organization account
    if (
      registerData.account_type === "organization" &&
      !registerData.organization_name.trim()
    ) {
      toast.error("Organization name required", {
        description: "Please enter your organization name",
      });
      return;
    }

    if (!registerData.agreeToTerms) {
      toast.error("Please agree to terms");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/register`,
        registerData,
      );
      clearActiveClient();
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      toast.success("Account created!", {
        description:
          registerData.account_type === "organization"
            ? "Welcome to Smart ITBox - Organization account created"
            : "Welcome to Smart ITBox",
      });

      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      toast.error("Registration failed", {
        description: error.response?.data?.detail || "Please try again",
      });
    } finally {
      setIsLoading(false);
      setCurrentStep(1);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Email required", {
        description: "Please enter your email address",
      });
      return;
    }
    setSendingReset(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email: forgotEmail,
      });
      toast.success("Reset link sent", {
        description: "Check your email for password reset instructions",
      });
      setForgotPasswordOpen(false);
      setForgotEmail("");
    } catch (error) {
      toast.error("Failed to send reset link", {
        description: error.response?.data?.detail || "Please try again later",
      });
    } finally {
      setSendingReset(false);
    }
  };

  const PasswordStrength = ({ password }) => {
    const checks = [
      { regex: /.{8,}/, label: "8+ characters" },
      { regex: /[A-Z]/, label: "Uppercase" },
      { regex: /[a-z]/, label: "Lowercase" },
      { regex: /[0-9]/, label: "Number" },
      { regex: /[^A-Za-z0-9]/, label: "Special" },
    ];

    const strength = checks.filter((check) =>
      check.regex.test(password),
    ).length;
    const strengthLabels = ["Very Weak", "Weak", "Fair", "Good", "Excellent"];
    const strengthColors = [
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-lime-500",
      "bg-green-500",
    ];

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">Password strength</span>
          <span
            className={`text-xs font-medium ${strength >= 4 ? "text-green-600" : strength >= 3 ? "text-yellow-600" : "text-red-600"}`}
          >
            {strengthLabels[strength - 1] || "None"}
          </span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${level <= strength ? strengthColors[strength - 1] : "bg-gray-200"}`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {checks.map((check, index) => (
            <div
              key={index}
              className="flex items-center gap-1"
              title={check.label}
            >
              {check.regex.test(password) ? (
                <CheckCircle
                  size={10}
                  className="text-green-500 flex-shrink-0"
                />
              ) : (
                <div className="w-2 h-2 rounded-full border border-gray-300 flex-shrink-0"></div>
              )}
              <span
                className={`text-[10px] ${check.regex.test(password) ? "text-green-600" : "text-gray-500"}`}
              >
                {check.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const pageVariants = {
    initial: { opacity: 0, scale: 0.95 },
    in: { opacity: 1, scale: 1 },
    out: { opacity: 0, scale: 1.05 },
  };

  const heroVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const heroItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, delay: 0.3, ease: "easeOut" },
    },
  };

  const Stepper = ({ currentStep, totalSteps }) => {
    return (
      <div className="flex items-center justify-center mb-4">
        {[...Array(totalSteps)].map((_, index) => (
          <React.Fragment key={index}>
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${currentStep > index + 1 ? "bg-gradient-to-r from-primary-500 to-orange-500 text-white" : currentStep === index + 1 ? "border-2 border-primary-500 text-primary-600" : "border border-gray-300 text-gray-400"}`}
            >
              {currentStep > index + 1 ? <CheckCircle size={10} /> : index + 1}
            </div>
            {index < totalSteps - 1 && (
              <div
                className={`w-8 h-0.5 ${currentStep > index + 1 ? "bg-gradient-to-r from-primary-500 to-orange-500" : "bg-gray-300"}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="login-page"
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
        transition={{ duration: 0.8, ease: "anticipate" }}
        data-testid="login-page"
      >
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-primary-50/30" />

          <motion.div
            className="absolute w-[400px] h-[400px] rounded-full"
            animate={{
              background: [
                "radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, transparent 70%)",
                "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
                "radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, transparent 70%)",
              ],
            }}
            transition={{ duration: 8, repeat: Infinity, delay: 1 }}
            style={{
              bottom: "15%",
              right: "15%",
            }}
          />

          {/* Enhanced Floating Particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              animate={{
                y: [0, -15, 0],
                x: [0, Math.sin(i) * 8, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2.5 + Math.random() * 1.5,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: "easeInOut",
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background:
                  i % 3 === 0
                    ? "radial-gradient(circle, rgba(59, 130, 246, 0.2), transparent)"
                    : i % 3 === 1
                      ? "radial-gradient(circle, rgba(249, 115, 22, 0.2), transparent)"
                      : "radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent)",
              }}
            />
          ))}
        </div>

        <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-center relative z-10">
          <motion.div
            className="hidden lg:block"
            variants={heroVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="mb-6" variants={heroItemVariants}>
              <div className="flex items-center gap-3 mb-6">
                <div className="relative">
                  <img
                    src="/logo.png"
                    alt="Smart ITBox logo"
                    className="h-10"
                  />
                </div>
                <div className="w-0.5 h-6 bg-gray-300" />
                <div>
                  <div className="text-sm font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                    SmartCA Platform
                  </div>
                  <div className="text-xs text-gray-500">
                    Powered by Smart ITBox
                  </div>
                </div>
              </div>

              <motion.h1
                className="text-4xl font-bold text-gray-900 leading-tight mb-3"
                variants={heroItemVariants}
              >
                Intelligent{" "}
                <motion.span
                  className="bg-gradient-to-r from-primary-500 via-orange-500 to-amber-500 bg-clip-text text-transparent"
                  animate={{
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  GST & Bank Reconciliation
                </motion.span>
              </motion.h1>

              <motion.p
                className="text-base text-gray-600 leading-relaxed max-w-lg"
                variants={heroItemVariants}
              >
                Transform your financial workflow with AI-powered automation.
                Extract, verify, and reconcile invoices with bank-grade
                accuracy.
              </motion.p>
            </motion.div>

            <motion.div className="space-y-4" variants={heroItemVariants}>
              {/* Enhanced Feature Grid with Icons */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    title: "AI-Powered Extraction",
                    description: "99.5% accuracy rate",
                    icon: Cpu,
                    color: "primary",
                    gradient: "from-primary-500 to-primary-600",
                  },
                  {
                    title: "Bank-Grade Security",
                    description: "256-bit AES encryption",
                    icon: Shield,
                    color: "orange",
                    gradient: "from-orange-500 to-orange-600",
                  },
                  {
                    title: "Real-time Processing",
                    description: "Instant verification",
                    icon: Zap,
                    color: "primary",
                    gradient: "from-primary-500 to-primary-600",
                  },
                  {
                    title: "Cloud Sync",
                    description: "Access anywhere",
                    icon: Cloud,
                    color: "orange",
                    gradient: "from-orange-500 to-orange-600",
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="group relative overflow-hidden rounded-xl p-3 border border-gray-200 bg-white/60 backdrop-blur-sm hover:bg-white hover:shadow-lg transition-all duration-300"
                    variants={heroItemVariants}
                    whileHover={{ y: -4, scale: 1.02 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Gradient Accent */}
                    <div
                      className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${feature.gradient}`}
                    ></div>

                    <div className="flex items-start gap-3 pl-1">
                      <div
                        className={`p-2 rounded-lg bg-gradient-to-br from-${feature.color}-100 to-${feature.color}-50`}
                      >
                        <feature.icon
                          size={16}
                          className={`text-${feature.color}-600`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-800 mb-0.5">
                          {feature.title}
                        </div>
                        <div className="text-xs text-gray-600">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Compact Form Section */}
          <motion.div
            className="flex justify-center lg:justify-end"
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            <Card className="bg-gradient-to-br from-white to-gray-50/80 w-full shadow-none border border-gray-100 max-w-sm overflow-hidden">
              <CardHeader className="pb-4 relative z-10">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-orange-500 mb-2">
                    <Fingerprint size={16} className="text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {activeTab === "login" ? "Welcome Back" : "Get Started"}
                  </CardTitle>
                  <CardDescription className="text-gray-600 text-xs mt-1">
                    {activeTab === "login"
                      ? "Sign in to continue"
                      : "Create your account"}
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="pt-0 relative z-10">
                <Tabs
                  value={activeTab}
                  onValueChange={(value) => {
                    setActiveTab(value);
                    if (value === "register") setCurrentStep(1);
                  }}
                  className="w-full"
                >
                  <TabsList className="grid grid-cols-2 mb-6 bg-gray-100/50 p-1 rounded-lg">
                    <TabsTrigger
                      value="login"
                      className="text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger
                      value="register"
                      className="text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
                    >
                      Register
                    </TabsTrigger>
                  </TabsList>

                  {/* Login Form - Compact */}
                  <TabsContent value="login" className="space-y-3">
                    <form onSubmit={handleLogin} className="space-y-3">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="login-email"
                          className="text-xs font-medium text-gray-700"
                        >
                          Email
                        </Label>
                        <div className="relative group">
                          <Mail
                            size={12}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                          />
                          <Input
                            id="login-email"
                            type="email"
                            data-testid="login-email-input"
                            placeholder="email@company.com"
                            value={loginData.email}
                            onChange={(e) =>
                              setLoginData({
                                ...loginData,
                                email: e.target.value,
                              })
                            }
                            className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between">
                          <Label
                            htmlFor="login-password"
                            className="text-xs font-medium text-gray-700"
                          >
                            Password
                          </Label>
                          <button
                            type="button"
                            onClick={() => setForgotPasswordOpen(true)}
                            className="text-xs bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent hover:opacity-80"
                            data-testid="forgot-password-link"
                          >
                            Forgot?
                          </button>
                        </div>
                        <div className="relative group">
                          <Lock
                            size={12}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                          />
                          <Input
                            id="login-password"
                            type={showPassword.login ? "text" : "password"}
                            data-testid="login-password-input"
                            placeholder="••••••••"
                            value={loginData.password}
                            onChange={(e) =>
                              setLoginData({
                                ...loginData,
                                password: e.target.value,
                              })
                            }
                            className="pl-8 pr-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                            required
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowPassword({
                                ...showPassword,
                                login: !showPassword.login,
                              })
                            }
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            data-testid="toggle-login-password"
                          >
                            {showPassword.login ? (
                              <EyeOff size={12} />
                            ) : (
                              <Eye size={12} />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between py-1">
                        <div className="flex items-center space-x-1.5">
                          <Checkbox
                            id="remember"
                            checked={rememberMe}
                            onCheckedChange={setRememberMe}
                            className="h-3 w-3 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-primary-500 data-[state=checked]:to-orange-500"
                          />
                          <Label
                            htmlFor="remember"
                            className="text-xs text-gray-700"
                          >
                            Remember me
                          </Label>
                        </div>
                      </div>

                      <motion.button
                        type="submit"
                        disabled={isLoading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-primary-500 to-orange-500 text-white font-medium text-xs hover:from-primary-600 hover:to-orange-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-1 group"
                        data-testid="login-submit-btn"
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-1.5">
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Signing in...
                          </span>
                        ) : (
                          <>
                            Sign In
                            <ArrowRight
                              size={12}
                              className="group-hover:translate-x-0.5 transition-transform"
                            />
                          </>
                        )}
                      </motion.button>

                      <div className="text-center text-xs text-gray-500 pt-3 border-t border-gray-200">
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setActiveTab("register")}
                          className="font-medium bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent hover:opacity-80"
                        >
                          Register
                        </button>
                      </div>
                    </form>
                  </TabsContent>

                  {/* Register Form with Stepper - Compact */}
                  <TabsContent value="register" className="space-y-3">
                    <form onSubmit={handleRegister} className="space-y-3">
                      {/* Stepper */}
                      <Stepper currentStep={currentStep} totalSteps={3} />

                      {/* Step 1: Account Type & Basic Info */}
                      <AnimatePresence mode="wait">
                        {currentStep === 1 && (
                          <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="space-y-3"
                          >
                            <div className="space-y-1.5">
                              <Label className="text-xs font-medium text-gray-700">
                                Account Type
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setRegisterData({
                                      ...registerData,
                                      account_type: "individual",
                                    })
                                  }
                                  className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border transition-all text-xs ${
                                    registerData.account_type === "individual"
                                      ? "border-primary-500 bg-gradient-to-r from-primary-50/50 to-orange-50/50 text-gray-800 shadow-sm"
                                      : "border-gray-300 hover:border-gray-400 text-gray-600 hover:bg-gray-50"
                                  }`}
                                  data-testid="account-type-individual"
                                >
                                  <User
                                    size={12}
                                    className={
                                      registerData.account_type === "individual"
                                        ? "text-primary-600"
                                        : "text-gray-500"
                                    }
                                  />
                                  <span>Individual</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setRegisterData({
                                      ...registerData,
                                      account_type: "organization",
                                    })
                                  }
                                  className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border transition-all text-xs ${
                                    registerData.account_type === "organization"
                                      ? "border-primary-500 bg-gradient-to-r from-primary-50/50 to-orange-50/50 text-gray-800 shadow-sm"
                                      : "border-gray-300 hover:border-gray-400 text-gray-600 hover:bg-gray-50"
                                  }`}
                                  data-testid="account-type-organization"
                                >
                                  <Building
                                    size={12}
                                    className={
                                      registerData.account_type ===
                                      "organization"
                                        ? "text-primary-600"
                                        : "text-gray-500"
                                    }
                                  />
                                  <span>Organization</span>
                                </button>
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label
                                htmlFor="register-name"
                                className="text-xs font-medium text-gray-700"
                              >
                                Full Name
                              </Label>
                              <div className="relative group">
                                <User
                                  size={12}
                                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                                />
                                <Input
                                  id="register-name"
                                  type="text"
                                  data-testid="register-name-input"
                                  placeholder="John Doe"
                                  value={registerData.name}
                                  onChange={(e) =>
                                    setRegisterData({
                                      ...registerData,
                                      name: e.target.value,
                                    })
                                  }
                                  className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                                  required
                                />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label
                                htmlFor="register-email"
                                className="text-xs font-medium text-gray-700"
                              >
                                Email
                              </Label>
                              <div className="relative group">
                                <Mail
                                  size={12}
                                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                                />
                                <Input
                                  id="register-email"
                                  type="email"
                                  data-testid="register-email-input"
                                  placeholder="email@company.com"
                                  value={registerData.email}
                                  onChange={(e) =>
                                    setRegisterData({
                                      ...registerData,
                                      email: e.target.value,
                                    })
                                  }
                                  className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                                  required
                                />
                              </div>
                            </div>

                            <Button
                              type="button"
                              onClick={() => setCurrentStep(2)}
                              disabled={
                                !registerData.name || !registerData.email
                              }
                              className="w-full py-2 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
                            >
                              Next: Set Password
                              <ChevronRight size={12} className="ml-1" />
                            </Button>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Step 2: Password */}
                      <AnimatePresence mode="wait">
                        {currentStep === 2 && (
                          <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="space-y-3"
                          >
                            <div className="space-y-1.5">
                              <Label
                                htmlFor="register-password"
                                className="text-xs font-medium text-gray-700"
                              >
                                Password
                              </Label>
                              <div className="relative group">
                                <Lock
                                  size={12}
                                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                                />
                                <Input
                                  id="register-password"
                                  type={
                                    showPassword.register ? "text" : "password"
                                  }
                                  data-testid="register-password-input"
                                  placeholder="••••••••"
                                  value={registerData.password}
                                  onChange={(e) =>
                                    setRegisterData({
                                      ...registerData,
                                      password: e.target.value,
                                    })
                                  }
                                  className="pl-8 pr-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                                  required
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowPassword({
                                      ...showPassword,
                                      register: !showPassword.register,
                                    })
                                  }
                                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                  data-testid="toggle-register-password"
                                >
                                  {showPassword.register ? (
                                    <EyeOff size={12} />
                                  ) : (
                                    <Eye size={12} />
                                  )}
                                </button>
                              </div>
                              <PasswordStrength
                                password={registerData.password}
                              />
                            </div>

                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setCurrentStep(1)}
                                className="flex-1 py-2 text-xs border-gray-300 hover:border-gray-400"
                              >
                                <ChevronLeft size={12} className="mr-1" />
                                Back
                              </Button>
                              <Button
                                type="button"
                                onClick={() => setCurrentStep(3)}
                                disabled={registerData.password.length < 8}
                                className="flex-1 py-2 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
                              >
                                Next: Final Step
                                <ChevronRight size={12} className="ml-1" />
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Step 3: Organization Details & Terms */}
                      <AnimatePresence mode="wait">
                        {currentStep === 3 && (
                          <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="space-y-3"
                          >
                            {/* Organization Fields (only for organization accounts) */}
                            {registerData.account_type === "organization" && (
                              <>
                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="register-org-name"
                                    className="text-xs font-medium text-gray-700"
                                  >
                                    Organization Name{" "}
                                    <span className="text-red-500">*</span>
                                  </Label>
                                  <div className="relative group">
                                    <Building
                                      size={12}
                                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                                    />
                                    <Input
                                      id="register-org-name"
                                      type="text"
                                      data-testid="register-org-name-input"
                                      placeholder="Company Name"
                                      value={registerData.organization_name}
                                      onChange={(e) =>
                                        setRegisterData({
                                          ...registerData,
                                          organization_name: e.target.value,
                                        })
                                      }
                                      className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                                    />
                                  </div>
                                </div>

                                <div className="space-y-1.5">
                                  <Label
                                    htmlFor="register-gst"
                                    className="text-xs font-medium text-gray-700"
                                  >
                                    GST Number{" "}
                                    <span className="text-gray-400 text-xs">
                                      (Optional)
                                    </span>
                                  </Label>
                                  <div className="relative group">
                                    <FileCheck
                                      size={12}
                                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                                    />
                                    <Input
                                      id="register-gst"
                                      type="text"
                                      data-testid="register-gst-input"
                                      placeholder="22AAAAA0000A1Z5"
                                      value={registerData.gst_number}
                                      onChange={(e) =>
                                        setRegisterData({
                                          ...registerData,
                                          gst_number:
                                            e.target.value.toUpperCase(),
                                        })
                                      }
                                      className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent bg-white/50"
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="flex items-start space-x-1.5 py-1">
                              <Checkbox
                                id="terms"
                                checked={registerData.agreeToTerms}
                                onCheckedChange={(checked) =>
                                  setRegisterData({
                                    ...registerData,
                                    agreeToTerms: checked,
                                  })
                                }
                                className="h-3 w-3 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-primary-500 data-[state=checked]:to-orange-500"
                              />
                              <Label
                                htmlFor="terms"
                                className="text-xs text-gray-700 leading-tight"
                              >
                                I agree to the{" "}
                                <a
                                  href="#"
                                  className="font-medium bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent hover:opacity-80"
                                >
                                  Terms
                                </a>{" "}
                                and{" "}
                                <a
                                  href="#"
                                  className="font-medium bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent hover:opacity-80"
                                >
                                  Privacy Policy
                                </a>
                              </Label>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setCurrentStep(2)}
                                className="flex-1 py-2 text-xs border-gray-300 hover:border-gray-400"
                              >
                                <ChevronLeft size={12} className="mr-1" />
                                Back
                              </Button>
                              <motion.button
                                type="submit"
                                disabled={
                                  isLoading || !registerData.agreeToTerms
                                }
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-primary-500 to-orange-500 text-white font-medium text-xs hover:from-primary-600 hover:to-orange-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-1"
                                data-testid="register-submit-btn"
                              >
                                {isLoading ? (
                                  <span className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Creating...
                                  </span>
                                ) : (
                                  <>
                                    Create Account
                                    <ArrowRight size={12} className="ml-1" />
                                  </>
                                )}
                              </motion.button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="text-center text-xs text-gray-500 pt-3 border-t border-gray-200">
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setActiveTab("login")}
                          className="font-medium bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent hover:opacity-80"
                        >
                          Sign in
                        </button>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Enhanced Forgot Password Dialog */}
        <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
          <DialogContent className="sm:max-w-sm border-0 bg-gradient-to-br from-white to-gray-50/95 backdrop-blur-sm shadow-xl">
            <DialogHeader>
              <div className="mx-auto w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-orange-500 flex items-center justify-center mb-3">
                <KeyRound size={20} className="text-white" />
              </div>
              <DialogTitle className="text-lg text-center font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                Reset Password
              </DialogTitle>
              <DialogDescription className="text-center text-xs text-gray-600">
                Enter your email to receive a secure password reset link
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleForgotPassword} className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label
                  htmlFor="forgot-email"
                  className="text-xs font-medium text-gray-700"
                >
                  Email Address
                </Label>
                <div className="relative group">
                  <Mail
                    size={12}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                  />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-8 py-2 h-8 text-xs rounded-lg border border-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>
              </div>

              <DialogFooter className="gap-2 pt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotPasswordOpen(false)}
                  className="flex-1 py-2 text-xs rounded-lg border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sendingReset}
                  className="flex-1 py-2 text-xs rounded-lg bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg"
                  data-testid="send-reset-btn"
                >
                  {sendingReset ? (
                    <span className="flex items-center gap-1.5">
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Sending...
                    </span>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </DialogFooter>
            </form>

            <div className="mt-3 p-2 rounded-md bg-gradient-to-r from-primary-50/50 to-orange-50/50 border border-primary-100/50">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <ShieldCheck size={10} className="text-primary-500" />
                <span>Reset links expire in 24 hours for security</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </AnimatePresence>
  );
}
